import SchemaProvider from 'titus-ts/dist/js/MetadataRenderer/Schema/SchemaProvider';
import { OfficeApi } from '../../office/OfficeApi';
import { AuditService } from './baseAuditService';
import { IItemAuditDataProvider } from './auditDataProvider';
import { MessageAuditDataProvider } from './messageAuditDataProvider';
import { DocumentAuditDataProvider } from './documentAuditDataProvider';
import AppService from '../appService';
import { OfficeJsEnvironmentInfoProvider } from './environmentAuditDataProvider';
import { AuditObjectFilter } from './auditObjectFilter';
import { AppSettingsService } from '../appSettingsService';
import ServiceConstantsProvider from '../serviceConstantsProvider';
import { AuthService } from '../auth/authService';
import { FortraDlpAnalyticsAuditService } from './fortraDlpAnalyticsAuditService';
import { localSettingsService } from '../localSettingsService';
import { ConsoleAuditService } from './consoleAuditService';

export default class AuditServiceFactory {
    private officeApi: OfficeApi;
    private schemaProvider: SchemaProvider;
    private appService: AppService;
    private appSettingsService: AppSettingsService;
    private authService: AuthService;

    constructor(
        officeApi: OfficeApi,
        schemaProvider: SchemaProvider,
        appService: AppService,
        appSettingsService: AppSettingsService,
        authService: AuthService,
    ) {
        this.officeApi = officeApi;
        this.schemaProvider = schemaProvider;
        this.appService = appService;
        this.appSettingsService = appSettingsService;
        this.authService = authService;
    }

    public getAuditService = async (): Promise<AuditService | null> => {
        let itemDataProvider: IItemAuditDataProvider;
        switch (this.officeApi.getHost()) {
            case 'Outlook':
                itemDataProvider = new MessageAuditDataProvider(this.officeApi, this.schemaProvider, this.appService);
                break;
            case 'Excel':
            case 'Word':
            case 'PowerPoint':
                itemDataProvider = new DocumentAuditDataProvider(this.officeApi, this.schemaProvider, this.appService);
                break;
            default:
                throw new Error('Failed to create audit service. Unsupported host');
                break;
        }

        const appSettings = await this.appSettingsService.getApplicationSettings();

        const officeJsEnvironmentInfoProvider = new OfficeJsEnvironmentInfoProvider();
        const auditFilterService = new AuditObjectFilter(appSettings.AuditFilter?.Properties);

        if (localSettingsService.getLocalSettings().useConsoleAuditer) {
            console.log('Using the console auditer, all events will be sent to the browser console log ONLY.');

            return new ConsoleAuditService(
                this.authService,
                this.appSettingsService,
                officeJsEnvironmentInfoProvider,
                itemDataProvider,
                auditFilterService,
            ) as AuditService;
        } else if (
            appSettings.AuditLogSeverity?.EnabledLoggers?.includes(
                FortraDlpAnalyticsAuditService.policyManagerLoggerName,
            )
        ) {
            console.log('Using the Fortra DLP Analytics and Reporting service for auditing.');

            return new FortraDlpAnalyticsAuditService(
                ServiceConstantsProvider.getEventCollectorUrl(),
                this.authService,
                this.appSettingsService,
                officeJsEnvironmentInfoProvider,
                itemDataProvider,
                auditFilterService,
            ) as AuditService;
        }

        console.log('No auditing service is configured audit events will not be produced.');
        return null;
    };
}
